
import { Link, useLocation } from "react-router-dom";
import { Home, FileText, Settings, BookOpen, Languages } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/components/auth/AuthProvider";
import { Logo } from "@/components/shared/Logo";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect } from "react";

const Sidebar = () => {
  const location = useLocation();
  const { profile, loading } = useAuth();
  const isSuperAdmin = profile?.role === 'super-admin';

  useEffect(() => {
    console.log('Sidebar - Auth state:', { 
      profileRole: profile?.role,
      isSuperAdmin,
      showTrainingLink: isSuperAdmin,
      loading
    });
  }, [profile, isSuperAdmin, loading]);

  let menuItems = [
    { icon: Home, label: "Dashboard", href: "/dashboard" },
    { 
      icon: FileText, 
      label: "Collections", 
      href: "/collections",
      matchRoutes: ["/collections", "/collections/"] // Routes that should highlight this menu item
    },
    { 
      icon: Languages, 
      label: "Translations", 
      href: "/translations",
      matchRoutes: ["/translations", "/translations/"] // Routes that should highlight this menu item
    },
    { icon: Settings, label: "Settings", href: "/settings" }
  ];

  if (isSuperAdmin) {
    console.log('Adding Training Data menu item for super-admin');
    menuItems.push({ icon: BookOpen, label: "Training Data", href: "/training" });
  }

  if (loading) {
    return (
      <div className="w-64 bg-gradient-to-b from-background via-card/50 to-background fixed top-0 left-0 h-screen border-r border-border transition-colors">
        <div className="p-6">
          <Skeleton className="h-8 w-32" />
        </div>
        <nav className="px-3 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </nav>
      </div>
    );
  }

  const isRouteActive = (href: string, matchRoutes?: string[]) => {
    const currentPath = location.pathname;
    
    // Exact match for the href
    if (currentPath === href) return true;
    
    // Check additional matching routes
    if (matchRoutes) {
      return matchRoutes.some(route => 
        currentPath.startsWith(route) && 
        (currentPath === route || currentPath.startsWith(route + '/'))
      );
    }
    
    return false;
  };

  return (
    <div className="w-64 bg-gradient-to-b from-background via-card/50 to-background fixed top-0 left-0 h-screen border-r border-border transition-colors">
      <div className="p-6 flex items-center">
        <Link to="/dashboard">
          <Logo 
            showTagline={false} 
            variant="darkGray" 
            size="medium-small" 
          />
        </Link>
      </div>

      <nav className="px-3">
        {menuItems.map(({ icon: Icon, label, href, matchRoutes }) => (
          <Link
            key={href}
            to={href}
            className={cn(
              "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors",
              isRouteActive(href, matchRoutes)
                ? "bg-primary/10 text-primary"
                : "hover:bg-card"
            )}
          >
            <Icon className="w-5 h-5" />
            <span>{label}</span>
          </Link>
        ))}
      </nav>
      
      {profile && (
        <div className="absolute bottom-4 left-4 text-xs text-muted-foreground">
          Role: {profile.role || 'unknown'}
        </div>
      )}
    </div>
  );
};

export default Sidebar;
